import { useCallback, useEffect, useRef, useState } from 'react';
import { useNotificationsApi } from '@backstage/plugin-notifications';
import { useSignal } from '@backstage/plugin-signals-react';
import type { CreatedAfterKey, NotificationsFilters, SortByKey } from './types';

const CREATED_AFTER: Record<CreatedAfterKey, () => Date> = {
  last24h: () => new Date(Date.now() - 24 * 3600 * 1000),
  lastWeek: () => new Date(Date.now() - 7 * 24 * 3600 * 1000),
  all: () => new Date(0),
};

const SORT_BY: Record<
  SortByKey,
  { sort: 'created' | 'topic' | 'origin'; sortOrder: 'asc' | 'desc' }
> = {
  newest: { sort: 'created', sortOrder: 'desc' },
  oldest: { sort: 'created', sortOrder: 'asc' },
  topic: { sort: 'topic', sortOrder: 'asc' },
  origin: { sort: 'origin', sortOrder: 'asc' },
};

const DEFAULT_FILTERS: NotificationsFilters = {
  view: 'unread',
  createdAfter: 'all',
  sortBy: 'newest',
  severity: 'low',
};

/** Signals arrive one per notification; coalesce a burst into one refetch. */
const REFRESH_THROTTLE_MS = 2000;

/**
 * Filter, paging and fetch state for the notifications page.
 *
 * The stock page holds the same state inline, but its filter panel is not
 * exported, so the Hone page rebuilds it against the public `NotificationsApi`.
 * Query shapes match upstream, so the results are the same.
 */
export function useNotificationsFeed() {
  const [filters, setFilters] = useState<NotificationsFilters>(DEFAULT_FILTERS);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [containsText, setContainsText] = useState<string | undefined>();
  const [refreshToken, setRefreshToken] = useState(0);
  const lastRefreshAt = useRef(0);
  const trailingTimer = useRef<ReturnType<typeof setTimeout>>();
  const { lastSignal } = useSignal('notifications');

  const { view, createdAfter, sortBy, severity, topic } = filters;

  const { value, loading, error, retry } = useNotificationsApi(
    api => {
      const since = CREATED_AFTER[createdAfter]();
      const options = {
        search: containsText,
        limit: pageSize,
        offset: page * pageSize,
        minimumSeverity: severity,
        ...SORT_BY[sortBy],
        ...(view === 'unread' ? { read: false } : {}),
        ...(view === 'read' ? { read: true } : {}),
        ...(view === 'saved' ? { saved: true } : {}),
        ...(topic ? { topic } : {}),
        ...(since.valueOf() > 0 ? { createdAfter: since } : {}),
      };
      return Promise.all([
        api.getNotifications(options),
        api.getStatus(),
        api.getTopics(options),
      ]);
    },
    [
      containsText,
      view,
      createdAfter,
      page,
      pageSize,
      sortBy,
      severity,
      topic,
      refreshToken,
    ],
  );

  // Leading + trailing throttle: fetch immediately on the first signal, then once
  // more at the end of the window so the final notifications in a burst (which may
  // not have been persisted when the leading fetch ran) are not silently dropped.
  const refetch = useCallback(() => {
    const now = Date.now();
    const sinceLast = now - lastRefreshAt.current;
    if (sinceLast >= REFRESH_THROTTLE_MS) {
      lastRefreshAt.current = now;
      retry();
      return;
    }
    if (trailingTimer.current) {
      return;
    }
    trailingTimer.current = setTimeout(() => {
      trailingTimer.current = undefined;
      lastRefreshAt.current = Date.now();
      retry();
    }, REFRESH_THROTTLE_MS - sinceLast);
  }, [retry]);

  useEffect(() => {
    if (lastSignal && (lastSignal as { action?: string }).action) {
      refetch();
    }
  }, [lastSignal, refetch]);

  useEffect(
    () => () => {
      if (trailingTimer.current) {
        clearTimeout(trailingTimer.current);
      }
    },
    [],
  );

  // Changing a filter with the cursor further in would land on an empty page.
  const onFiltersChange = useCallback((next: NotificationsFilters) => {
    setFilters(next);
    setPage(0);
  }, []);

  const onSearchChange = useCallback((text: string) => {
    setContainsText(text || undefined);
    setPage(0);
  }, []);

  /*
   * Marking rows read or saved changes which of them the current view matches,
   * so the query is re-run rather than throttled — the table has already
   * applied the change optimistically.
   */
  const onUpdate = useCallback(() => setRefreshToken(n => n + 1), []);

  return {
    filters,
    onFiltersChange,
    page,
    setPage,
    pageSize,
    setPageSize,
    onSearchChange,
    onUpdate,
    loading,
    error,
    notifications: value?.[0]?.notifications,
    totalCount: value?.[0]?.totalCount ?? 0,
    isUnread: !!value?.[1]?.unread,
    allTopics: value?.[2]?.topics ?? [],
    /*
     * The text filter lives inside the table's own toolbar, so a no-result text
     * search has to keep the table mounted — swapping in the empty-state card
     * would take the field the reader needs to clear away with it.
     */
    hasSearchText: !!containsText,
    /** True only when nothing narrows the query, so the empty state can say so. */
    isUnfiltered:
      !containsText &&
      view === DEFAULT_FILTERS.view &&
      createdAfter === DEFAULT_FILTERS.createdAfter &&
      severity === DEFAULT_FILTERS.severity &&
      !topic,
  };
}
